/**
 * Convert stored VA proxy (HOST:PORT:USERNAME:PASSWORD) to GoLogin-style export URL.
 * Example: v2.proxyempire.io:5000:user:pass → http://user:pass@v2.proxyempire.io:5000
 */
export function formatProxyForVaExport(stored) {
  const s = String(stored || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;

  const parts = s.split(':');
  if (parts.length < 4) return '';

  const host = (parts[0] || '').trim();
  const port = (parts[1] || '').trim();
  const username = (parts[2] || '').trim();
  const password = (parts[3] || '').trim();

  if (!host || !port || !username || !password) return '';

  return `http://${username}:${password}@${host}:${port}`;
}

function escapeCsvField(value) {
  const str = String(value ?? '');
  if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

export function buildVaAccountsCsvRows(accounts) {
  const header = 'name,proxy,os';
  const lines = accounts.map((account) => [
    escapeCsvField(account.username),
    escapeCsvField(formatProxyForVaExport(account.proxy)),
    'mac'
  ].join(','));
  return [header, ...lines].join('\n');
}

export function downloadVaAccountsCsv(accounts, filename) {
  const csv = buildVaAccountsCsvRows(accounts);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `va-accounts-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Colon export: username:password:phone:ct0:auth:proxy
 * Proxy is stored as HOST:PORT:USERNAME:PASSWORD (additional colons in the line).
 */
export function resolveVaExportLogin(account, { usePlusUsername = false } = {}) {
  if (usePlusUsername) {
    const plus = String(account?.plusUsername || '').replace(/^@/, '').trim();
    if (plus) return plus;
  }
  return String(account?.username || '').trim();
}

export function buildVaAccountsColonExportLine(account, { usePlusUsername = false } = {}) {
  const login = resolveVaExportLogin(account, { usePlusUsername });
  return [
    login,
    account?.password || '',
    account?.phone || '',
    account?.ct0 || '',
    account?.authToken || '',
    account?.proxy || ''
  ].join(':');
}

export function buildVaAccountsColonExportText(accounts, options = {}) {
  return (accounts || [])
    .map((account) => buildVaAccountsColonExportLine(account, options))
    .join('\n');
}

export function downloadVaAccountsColonExport(accounts, options = {}) {
  const text = buildVaAccountsColonExportText(accounts, options);
  const suffix = options.usePlusUsername ? 'plus-username' : 'username';
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = options.filename || `va-accounts-${suffix}-${new Date().toISOString().slice(0, 10)}.txt`;
  link.click();
  URL.revokeObjectURL(url);
}
